import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Skeleton, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import * as Sentry from "@sentry/react";
import { AttachMoney, BusinessOutlined, Close as CloseIcon, DescriptionOutlined } from "@mui/icons-material";
import {
  Button,
  Callout,
  FieldWrapper,
  OptionCardGroup,
  OptionCardItem,
  PageHeader,
  StepActions,
} from "@components/ui_v2";
import { PaperGarantia } from "@components/ui_v2/garantias/PaperGarantia";
import { ButtonAddSlot } from "@components/ui_v2/garantias/ButtonAddSlot";
import { FormGarantiaInmobiliariaV2, UbicacionGarantia } from "@components/ui_v2/garantias/FormGarantiaInmobiliariaV2";
import { FormGarantiaPrendariaV2 } from "@components/ui_v2/garantias/FormGarantiaPrendariaV2";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import {
  useLazyGetGuaranteeConfigQuery,
  useLazyGetVehiclesByYearAndMakeQuery,
  useLazyGetVehiclesByYearMakeAndModelQuery,
  useLazyGetVehiclesByYearQuery,
  useLazyGetVehiclesYearsQuery,
} from "@api/catalogs";
import {
  useDeleteGuaranteesTypesMutation,
  useLazyGetGuaranteeTypeDetailConfigQuery,
  useLazyGetGuaranteesDataQuery,
  usePatchGuaranteesTypesMutation,
  usePostGuaranteesTypesMutation,
} from "@api/garantiasApi";
import { usePostValidateNextStageMutation, usePutApplicationCompletedMutation } from "@api/applications";
import { GuaranteeConfigStruture, GuaranteeVehicleDetailConfig, VehicleStrutureCatalog } from "@interfaces/catalogs";
import { ApiError, GarantiasVehicularInmobiliariaResponse } from "@interfaces/response";
import { PayloadRequestGarantiasVehicularInmobiliaria } from "@interfaces/request";
import { GUARANTEE_TYPE, STATUS_CATALOG } from "@helpers/constantes";
import useStatusConfig from "@hooks/useStatusConfig";
import {
  cleanAmount,
  detailNameOf,
  emptyGarantia,
  EMPTY_VEHICLE_CODES,
  formKindOf,
  initialGuaranteeTypeId,
  mapInmobiliariaGuarantee,
  mapVehicularGuarantee,
  MAX_GUARANTEES,
  selectActiveGuarantees,
  VEHICULOS_MANUAL,
} from "@helpers/informacionMultiGarantiasHelpers";
import {
  GarantiaFieldBindings,
  GarantiaFormValue,
  GuaranteeFormKind,
  VehicleCatalogCodes,
} from "@interfaces/informacionMultiGarantias";

const SCREEN_STATUS = STATUS_CATALOG.informacion_garantia.code;

/**
 * Tarjeta del tipo de garantía (`S_Garantia`): inmobiliaria y prendaria con el texto del diseño; los demás tipos del
 * catálogo (vehicular, cuentas por cobrar…) con su nombre.
 */
const guaranteeTypeCard = (code: string, name: string, kind: GuaranteeFormKind) => {
  if (kind === "inmobiliaria") {
    return {
      label: code === GUARANTEE_TYPE.inmobiliaria.code ? "Inmobiliaria" : name,
      sub: "Hipoteca sobre un inmueble",
      icon: <BusinessOutlined />,
    };
  }
  if (kind === "vehicular") {
    return {
      label: code === GUARANTEE_TYPE.prendaria.code ? "Prendaria" : name,
      sub: "Prenda sobre un bien mueble",
      icon: <AttachMoney />,
    };
  }
  return { label: name, icon: <DescriptionOutlined /> };
};

export const InformacionMultiGarantias = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nextStatus, previousStatus } = useStatusConfig(SCREEN_STATUS);

  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [getGuaranteeTypeConfig] = useLazyGetGuaranteeConfigQuery();
  const [getGuaranteesData] = useLazyGetGuaranteesDataQuery();
  const [getGuaranteeTypeDetailConfig] = useLazyGetGuaranteeTypeDetailConfigQuery();
  const [triggerListVehicleYears] = useLazyGetVehiclesYearsQuery();
  const [triggerGetVehicleByYear] = useLazyGetVehiclesByYearQuery();
  const [triggerGetVehicleByYearAndBrand] = useLazyGetVehiclesByYearAndMakeQuery();
  const [triggerGetVehicleByYearBrandAndModel] = useLazyGetVehiclesByYearMakeAndModelQuery();
  const [postGuarantees] = usePostGuaranteesTypesMutation();
  const [patchGuarantees] = usePatchGuaranteesTypesMutation();
  const [deleteGuarantees] = useDeleteGuaranteesTypesMutation();
  const [putApplicationGuarantees] = usePutApplicationCompletedMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const [guaranteeConfigList, setGuaranteeConfigList] = useState<GuaranteeConfigStruture[]>([]);
  const [detailConfigByType, setDetailConfigByType] = useState<Record<string, GuaranteeVehicleDetailConfig[]>>({});
  const [listYears, setListYears] = useState<string[]>([]);

  // Catálogos encadenados de Libro Azul, por garantía (`localId`).
  const [brandsOptions, setBrandsOptions] = useState<Record<string, VehicleStrutureCatalog[]>>({});
  const [modelsOptions, setModelsOptions] = useState<Record<string, VehicleStrutureCatalog[]>>({});
  const [versionsOptions, setVersionsOptions] = useState<Record<string, VehicleStrutureCatalog[]>>({});
  const [loadingBrand, setLoadingBrand] = useState<Record<string, boolean>>({});
  const [loadingModel, setLoadingModel] = useState<Record<string, boolean>>({});
  const [loadingVersion, setLoadingVersion] = useState<Record<string, boolean>>({});

  // TODO: el legacy nunca pone `isLoading` en true antes de su carga inicial (solo lo apaga en el
  // `finally`), así que el skeleton inicial nunca se ve. Se replica el mismo comportamiento; el
  // select de tipo igual queda deshabilitado porque `canInteract` exige catálogo cargado.
  const [isLoading, setIsLoading] = useState(false);
  /** Carga de catálogos dentro de una garantía (año → marca → modelo → versión): solo deshabilita campos. */
  const [isLoadingCatalogs, setIsLoadingCatalogs] = useState(false);
  const [isDeletingOnTypeChange, setIsDeletingOnTypeChange] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);

  const [pendingTypeChange, setPendingTypeChange] = useState<{
    localId: string;
    nextGuaranteeTypeId: string;
  } | null>(null);

  // Caches vivos para las funciones async: evitan releer state obsoleto dentro de una misma cadena.
  const detailConfigRef = useRef<Record<string, GuaranteeVehicleDetailConfig[]>>({});
  const yearsRef = useRef<string[]>([]);

  const ensureDetailConfig = async (guaranteeTypeId: string) => {
    if (!guaranteeTypeId) return [];
    const cached = detailConfigRef.current[guaranteeTypeId];
    if (cached) return cached;

    const details = await getGuaranteeTypeDetailConfig({
      guarantee_type_id: guaranteeTypeId,
      taxcode: application.type,
    }).unwrap();

    detailConfigRef.current = { ...detailConfigRef.current, [guaranteeTypeId]: details };
    setDetailConfigByType(detailConfigRef.current);
    return details;
  };

  const ensureVehicleYears = async () => {
    if (yearsRef.current.length > 0) return yearsRef.current;
    const years = await triggerListVehicleYears().unwrap();
    yearsRef.current = years;
    setListYears(years);
    return years;
  };

  const formKind = (guaranteeTypeId: string) => formKindOf(guaranteeConfigList, guaranteeTypeId);
  const detailName = (guaranteeTypeId: string, detailId: string) =>
    detailNameOf(detailConfigByType[guaranteeTypeId], detailId);

  // El esquema se reconstruye cuando llegan los catálogos porque las ramas dependen de ellos
  // (qué tipo de garantía es y si el detalle prendario es manual o de Libro Azul).
  const validationSchema = useMemo(() => {
    const isInmobiliaria = (guaranteeTypeId: string) =>
      formKindOf(guaranteeConfigList, guaranteeTypeId) === "inmobiliaria";
    const isVehicular = (guaranteeTypeId: string) => formKindOf(guaranteeConfigList, guaranteeTypeId) === "vehicular";
    const isManual = (guaranteeTypeId: string, detailId: string) =>
      VEHICULOS_MANUAL.includes(detailNameOf(detailConfigByType[guaranteeTypeId], detailId));

    const requiredWhen = (predicate: (guaranteeTypeId: string) => boolean) =>
      yup.string().when("guarantee_type_id", {
        is: (guaranteeTypeId: string) => predicate(guaranteeTypeId),
        then: (schema) => schema.required("Campo requerido"),
        otherwise: (schema) => schema.notRequired(),
      });

    return yup.object().shape({
      garantias: yup.array().of(
        yup.object().shape({
          guarantee_type_id: yup.string().required("Campo requerido"),
          // Inmobiliaria
          type_inmobiliaria: requiredWhen(isInmobiliaria),
          valor_estimado: requiredWhen(isInmobiliaria),
          ubicacion: yup
            .string()
            .when("guarantee_type_id", {
              is: (guaranteeTypeId: string) => isInmobiliaria(guaranteeTypeId),
              then: (schema) => schema.required("Campo requerido"),
              otherwise: (schema) => schema.notRequired(),
            })
            .test(
              "has-coordinates",
              "Selecciona una dirección válida desde el listado o ubícala en el mapa",
              (value, ctx) => {
                const parent = ctx.parent as Partial<GarantiaFormValue> | undefined;
                if (!isInmobiliaria(parent?.guarantee_type_id ?? "")) return true;
                if (!value) return true;

                const lat = Number(parent?.latitud);
                const lng = Number(parent?.longitud);
                return Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0;
              },
            ),
          // Vehicular / prendaria
          type_vehicle: requiredWhen(isVehicular),
          year: requiredWhen(isVehicular),
          brand: requiredWhen(isVehicular),
          model: requiredWhen(isVehicular),
          version: requiredWhen(isVehicular),
          km: requiredWhen(isVehicular),
          estimated_value: yup.string().when(["guarantee_type_id", "type_vehicle"], {
            is: (guaranteeTypeId: string, detailId: string) =>
              isVehicular(guaranteeTypeId) && isManual(guaranteeTypeId, detailId),
            then: (schema) => schema.required("Campo requerido"),
            otherwise: (schema) => schema.notRequired(),
          }),
        }),
      ),
    });
  }, [guaranteeConfigList, detailConfigByType]);

  const buildGuaranteePayload = (item: GarantiaFormValue): PayloadRequestGarantiasVehicularInmobiliaria | null => {
    const kind = formKind(item.guarantee_type_id);

    if (kind === "inmobiliaria") {
      return {
        loan_application_id: application.id,
        guarantee_type_id: item.guarantee_type_id,
        guarantee_type_detail_id: item.type_inmobiliaria,
        estimated_value: cleanAmount(item.valor_estimado),
        latitude: Number(item.latitud),
        longitude: Number(item.longitud),
        address: item.ubicacion,
      };
    }

    if (kind === "vehicular") {
      const vehicleType = detailName(item.guarantee_type_id, item.type_vehicle);
      const manual = VEHICULOS_MANUAL.includes(vehicleType);

      return {
        loan_application_id: application.id,
        guarantee_type_id: item.guarantee_type_id,
        estimated_value: manual ? cleanAmount(item.estimated_value) : null,
        guarantee_type_detail_id: item.type_vehicle,
        details: {
          vehicle_type: vehicleType || "",
          guarantee_type_detail_id: manual ? null : item.type_vehicle,
          year: Number(item.year) || 0,
          make: item.brand_value || "",
          model: item.model_value || "",
          version: item.version_value || "",
          car_version_code: manual ? null : String(item.version),
          estimated_mileage: cleanAmount(item.km),
        },
      };
    }

    // Tipos sin formulario (ej. cuentas por cobrar): no se guarda garantía, solo viaja en `guarantee_types`.
    return null;
  };

  const formik = useFormik<{ garantias: GarantiaFormValue[] }>({
    initialValues: { garantias: [emptyGarantia()] },
    validateOnMount: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }

        // Cada garantía se guarda por separado: PATCH si ya existe en backend, POST si es nueva.
        for (const item of values.garantias) {
          const payload = buildGuaranteePayload(item);
          if (!payload) continue;

          if (item.guaranteeId) {
            await patchGuarantees({
              loan_application_id: application.id,
              guarantee_id: item.guaranteeId,
              data: payload,
            }).unwrap();
          } else {
            await postGuarantees({
              loan_application_id: application.id,
              data: { guarantees_list: [payload] },
            }).unwrap();
          }
        }

        const guaranteeTypes = Array.from(
          new Set(values.garantias.map((item) => item.guarantee_type_id).filter(Boolean)),
        );

        await putApplicationGuarantees({
          id_sol: application.id,
          body: { guarantee_types: guaranteeTypes },
        }).unwrap();

        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: nextStatus.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        navigate(nextStatus.url || "");
      } catch (error) {
        Sentry.captureException(error, {
          extra: { component: "InformacionMultiGarantias-submit", values: formik.values },
        });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const garantias = formik.values.garantias;

  const fieldError = (index: number, field: keyof GarantiaFormValue): string => {
    const errors = formik.errors.garantias as Array<Partial<Record<keyof GarantiaFormValue, string>>> | undefined;
    const touched = formik.touched.garantias as Array<Partial<Record<keyof GarantiaFormValue, boolean>>> | undefined;

    const message = errors?.[index]?.[field];
    return touched?.[index]?.[field] && message ? message : "";
  };

  const setItemField = (index: number, field: keyof GarantiaFormValue, value: string) =>
    formik.setFieldValue(`garantias[${index}].${field}`, value);

  const dropItemCatalogs = (localId: string) => {
    const drop = (prev: Record<string, unknown>) => {
      const next = { ...prev };
      delete next[localId];
      return next;
    };
    setBrandsOptions((prev) => drop(prev) as Record<string, VehicleStrutureCatalog[]>);
    setModelsOptions((prev) => drop(prev) as Record<string, VehicleStrutureCatalog[]>);
    setVersionsOptions((prev) => drop(prev) as Record<string, VehicleStrutureCatalog[]>);
  };

  /**
   * Resuelve los catálogos encadenados del Libro Azul para una garantía existente y los deja
   * cargados en los selects. Devuelve los códigos que el mapeo puro necesita.
   */
  const loadVehicleCatalogCodes = async (
    guarantee: GarantiasVehicularInmobiliariaResponse,
    localId: string,
  ): Promise<VehicleCatalogCodes> => {
    const year = guarantee.details?.year?.toString() || "";
    if (!year) return EMPTY_VEHICLE_CODES;

    const brandCatalog = await triggerGetVehicleByYear({ year }).unwrap();
    setBrandsOptions((prev) => ({ ...prev, [localId]: brandCatalog }));
    const brand = brandCatalog.find((item) => item.name === guarantee.details?.make)?.code || "";
    if (!brand) return EMPTY_VEHICLE_CODES;

    const modelCatalog = await triggerGetVehicleByYearAndBrand({ year, make: brand }).unwrap();
    setModelsOptions((prev) => ({ ...prev, [localId]: modelCatalog }));
    const model = modelCatalog.find((item) => item.name === guarantee.details?.model)?.code || "";
    if (!model) return { brand, model: "" };

    const versionCatalog = await triggerGetVehicleByYearBrandAndModel({ year, make: brand, model }).unwrap();
    setVersionsOptions((prev) => ({ ...prev, [localId]: versionCatalog }));

    return { brand, model };
  };

  /** Reconstruye una garantía existente del backend: carga sus catálogos y aplica el mapeo puro. */
  const hydrateGuarantee = async (
    guarantee: GarantiasVehicularInmobiliariaResponse,
    config: GuaranteeConfigStruture[],
  ): Promise<GarantiaFormValue> => {
    const guaranteeTypeId = guarantee.guarantee_type_id ?? "";
    const base: GarantiaFormValue = {
      ...emptyGarantia(),
      guaranteeId: guarantee.id,
      guarantee_type_id: guaranteeTypeId,
    };

    const kind = formKindOf(config, guaranteeTypeId);
    if (!kind) return base;

    const details = await ensureDetailConfig(guaranteeTypeId);
    if (kind === "inmobiliaria") return mapInmobiliariaGuarantee(guarantee, base);

    await ensureVehicleYears();

    const manual = VEHICULOS_MANUAL.includes(String(guarantee.details?.vehicle_type || ""));
    const codes = manual ? EMPTY_VEHICLE_CODES : await loadVehicleCatalogCodes(guarantee, base.localId);

    return mapVehicularGuarantee(guarantee, details, codes, base);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const config = await getGuaranteeTypeConfig({ tax_code: application.type }).unwrap();
        const guaranteeData = await getGuaranteesData({ loan_application_id: application.id }).unwrap();

        setGuaranteeConfigList(config);

        const activeGuarantees = selectActiveGuarantees(guaranteeData);
        if (activeGuarantees.length === 0) {
          // Sin garantías guardadas, la primera arranca con el tipo del producto o el elegido en el cotizador.
          const typeId = initialGuaranteeTypeId(config, application.guarantee_types);
          if (!typeId) return;
          formik.setValues({ garantias: [{ ...emptyGarantia(), guarantee_type_id: typeId }] });
          await ensureDetailConfig(typeId);
          if (formKindOf(config, typeId) === "vehicular") await ensureVehicleYears();
          return;
        }

        const items = await Promise.all(activeGuarantees.map((item) => hydrateGuarantee(item, config)));
        formik.setValues({ garantias: items });
      } catch (error) {
        Sentry.captureException(error, {
          extra: { component: "InformacionMultiGarantias-initial-data", values: { applicationId: application.id } },
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const guaranteeTypeOptions = useMemo(
    () =>
      guaranteeConfigList.map((item) => ({
        id: item.guarantee_type_id,
        name: item.guarantee_type.name,
        code: item.guarantee_type.code,
      })),
    [guaranteeConfigList],
  );

  // Con un solo tipo de garantía no se pregunta: todas las garantías son de ese tipo (`soloInmobiliaria` del diseño).
  const soloTypeId = guaranteeTypeOptions.length === 1 ? guaranteeTypeOptions[0].id : "";
  const soloInmobiliaria = Boolean(soloTypeId) && formKind(soloTypeId) === "inmobiliaria";
  const newGarantia = (): GarantiaFormValue => ({ ...emptyGarantia(), guarantee_type_id: soloTypeId });

  const canInteract = !isLoading && guaranteeTypeOptions.length > 0;
  const isBlocked = isLoading || isLoadingCatalogs || formik.isSubmitting || isDeletingOnTypeChange || isSkipping;

  /** Aplica el cambio de tipo: limpia la garantía y precarga los catálogos del tipo nuevo. */
  const applyGuaranteeTypeChange = async (localId: string, guaranteeTypeId: string) => {
    const index = garantias.findIndex((item) => item.localId === localId);
    if (index === -1) return;

    dropItemCatalogs(localId);
    await formik.setFieldValue(`garantias[${index}]`, {
      ...emptyGarantia(),
      localId,
      guarantee_type_id: guaranteeTypeId,
    });

    try {
      setIsLoadingCatalogs(true);
      await ensureDetailConfig(guaranteeTypeId);
      if (formKind(guaranteeTypeId) === "vehicular") {
        await ensureVehicleYears();
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: { component: "InformacionMultiGarantias-type-change", values: { guaranteeTypeId } },
      });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoadingCatalogs(false);
    }
  };

  const requestGuaranteeTypeChange = (localId: string, nextGuaranteeTypeId: string) => {
    if (formik.isSubmitting || isDeletingOnTypeChange) return;

    const item = garantias.find((garantia) => garantia.localId === localId);
    if (!item || nextGuaranteeTypeId === item.guarantee_type_id) return;

    // Si la garantía ya existe en backend, cambiar de tipo la elimina: se confirma antes.
    if (item.guaranteeId) {
      setPendingTypeChange({ localId, nextGuaranteeTypeId });
      return;
    }

    applyGuaranteeTypeChange(localId, nextGuaranteeTypeId);
  };

  const handleCloseTypeChangeDialog = () => {
    if (isDeletingOnTypeChange) return;
    setPendingTypeChange(null);
  };

  const handleConfirmTypeChange = async () => {
    if (!pendingTypeChange) return;

    const item = garantias.find((garantia) => garantia.localId === pendingTypeChange.localId);
    const backendGuaranteeId = item?.guaranteeId;

    // Por seguridad: si ya no hay id, solo cambia el tipo.
    if (!backendGuaranteeId) {
      applyGuaranteeTypeChange(pendingTypeChange.localId, pendingTypeChange.nextGuaranteeTypeId);
      setPendingTypeChange(null);
      return;
    }

    setIsDeletingOnTypeChange(true);
    try {
      await deleteGuarantees({
        loan_application_id: application.id,
        guarantee_id: backendGuaranteeId,
      }).unwrap();

      await applyGuaranteeTypeChange(pendingTypeChange.localId, pendingTypeChange.nextGuaranteeTypeId);
      setPendingTypeChange(null);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "InformacionMultiGarantias-change-guarantee-type",
          values: {
            localId: pendingTypeChange.localId,
            backendGuaranteeId,
            nextGuaranteeTypeId: pendingTypeChange.nextGuaranteeTypeId,
          },
        },
      });

      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      // Mantener el tipo actual (no se cambia nada)
      setPendingTypeChange(null);
    } finally {
      setIsDeletingOnTypeChange(false);
    }
  };

  const handleAddGarantia = () => {
    if (formik.isSubmitting || isDeletingOnTypeChange) return;
    if (garantias.length >= MAX_GUARANTEES) return;
    formik.setFieldValue("garantias", [...garantias, newGarantia()]);
  };

  const handleRemoveGarantia = async (localId: string) => {
    if (formik.isSubmitting || isDeletingOnTypeChange) return;

    const item = garantias.find((garantia) => garantia.localId === localId);
    if (!item) return;

    let canDelete = true;

    if (item.guaranteeId) {
      setIsLoading(true);
      try {
        await deleteGuarantees({
          loan_application_id: application.id,
          guarantee_id: item.guaranteeId,
        }).unwrap();
      } catch (error) {
        canDelete = false;

        Sentry.captureException(error, {
          extra: {
            component: "InformacionMultiGarantias-remove-guarantee",
            values: { localId, backendGuaranteeId: item.guaranteeId },
          },
        });

        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setIsLoading(false);
      }
    }

    if (!canDelete) return;

    dropItemCatalogs(localId);
    const next = garantias.filter((garantia) => garantia.localId !== localId);
    formik.setFieldValue("garantias", next.length > 0 ? next : [newGarantia()]);
  };

  const handleYearChange = async (index: number, value: string) => {
    const { localId } = garantias[index];
    try {
      setIsLoadingCatalogs(true);
      setLoadingBrand((prev) => ({ ...prev, [localId]: true }));
      setLoadingModel((prev) => ({ ...prev, [localId]: true }));
      setLoadingVersion((prev) => ({ ...prev, [localId]: true }));

      setItemField(index, "year", value);

      const brands = value ? await triggerGetVehicleByYear({ year: value }).unwrap() : [];

      setBrandsOptions((prev) => ({ ...prev, [localId]: brands }));
      setModelsOptions((prev) => ({ ...prev, [localId]: [] }));
      setVersionsOptions((prev) => ({ ...prev, [localId]: [] }));

      setItemField(index, "brand", "");
      setItemField(index, "brand_value", "");
      setItemField(index, "model", "");
      setItemField(index, "model_value", "");
      setItemField(index, "version", "");
      setItemField(index, "version_value", "");
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoadingCatalogs(false);
      setLoadingBrand((prev) => ({ ...prev, [localId]: false }));
      setLoadingModel((prev) => ({ ...prev, [localId]: false }));
      setLoadingVersion((prev) => ({ ...prev, [localId]: false }));
    }
  };

  const handleBrandChange = async (index: number, value: string) => {
    const { localId, year } = garantias[index];
    try {
      setIsLoadingCatalogs(true);
      setLoadingModel((prev) => ({ ...prev, [localId]: true }));
      setLoadingVersion((prev) => ({ ...prev, [localId]: true }));

      const selectedBrand = brandsOptions[localId]?.find((brand) => brand.code === value);
      setItemField(index, "brand", value);
      setItemField(index, "brand_value", selectedBrand?.name || "");

      const models = value ? await triggerGetVehicleByYearAndBrand({ year, make: value }).unwrap() : [];

      setModelsOptions((prev) => ({ ...prev, [localId]: models }));
      setVersionsOptions((prev) => ({ ...prev, [localId]: [] }));

      setItemField(index, "model", "");
      setItemField(index, "model_value", "");
      setItemField(index, "version", "");
      setItemField(index, "version_value", "");
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoadingCatalogs(false);
      setLoadingModel((prev) => ({ ...prev, [localId]: false }));
      setLoadingVersion((prev) => ({ ...prev, [localId]: false }));
    }
  };

  const handleModelChange = async (index: number, value: string) => {
    const { localId, year, brand } = garantias[index];
    try {
      setIsLoadingCatalogs(true);
      setLoadingVersion((prev) => ({ ...prev, [localId]: true }));

      const selectedModel = modelsOptions[localId]?.find((model) => model.code === value);
      setItemField(index, "model", value);
      setItemField(index, "model_value", selectedModel?.name || "");

      const versions = await triggerGetVehicleByYearBrandAndModel({ year, make: brand, model: value }).unwrap();

      setVersionsOptions((prev) => ({ ...prev, [localId]: versions }));

      setItemField(index, "version", "");
      setItemField(index, "version_value", "");
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoadingCatalogs(false);
      setLoadingVersion((prev) => ({ ...prev, [localId]: false }));
    }
  };

  const handleVersionChange = (index: number, value: string) => {
    const { localId } = garantias[index];
    const selectedVersion = versionsOptions[localId]?.find((version) => version.code === value);
    setItemField(index, "version", value);
    setItemField(index, "version_value", selectedVersion?.name || "");
  };

  /** Al cambiar de tipo prendario se limpia todo si cambia el modo de captura (manual ↔ Libro Azul). */
  const handleVehicleTypeChange = (index: number, value: string) => {
    const item = garantias[index];
    const wasManual = VEHICULOS_MANUAL.includes(detailName(item.guarantee_type_id, item.type_vehicle));
    const willBeManual = VEHICULOS_MANUAL.includes(detailName(item.guarantee_type_id, value));

    setItemField(index, "type_vehicle", value);

    if (wasManual !== willBeManual) {
      dropItemCatalogs(item.localId);
      setItemField(index, "year", "");
      setItemField(index, "brand", "");
      setItemField(index, "brand_value", "");
      setItemField(index, "model", "");
      setItemField(index, "model_value", "");
      setItemField(index, "version", "");
      setItemField(index, "version_value", "");
      setItemField(index, "km", "");
      setItemField(index, "estimated_value", "");
    }
  };

  const handleBack = () => {
    if (previousStatus) navigate(previousStatus.url);
  };

  /** "No tengo garantía": el backend quita el crédito con garantía al avanzar; aquí solo se cambia el status. */
  const handleSinGarantia = async () => {
    if (!nextStatus) return;
    setIsSkipping(true);
    try {
      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus.code,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();
      navigate(nextStatus.url || "");
    } catch (error) {
      Sentry.captureException(error, {
        extra: { component: "InformacionMultiGarantias-sin-garantia", values: { applicationId: application.id } },
      });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsSkipping(false);
    }
  };

  const isContinueDisabled =
    !canInteract ||
    isBlocked ||
    garantias.some((item) => !item.guarantee_type_id) ||
    !formik.isValid ||
    formik.isSubmitting;

  /** Ubicación inmobiliaria: con selección o mapa guarda coordenadas; con tipeo manual las limpia para no validar. */
  const handleUbicacionChange = (index: number, newValue: UbicacionGarantia) => {
    const isFromSelectionOrMap = newValue.lat !== 0 || newValue.lng !== 0;

    if (isFromSelectionOrMap) {
      formik.setValues(
        {
          garantias: garantias.map((garantia, garantiaIndex) =>
            garantiaIndex !== index
              ? garantia
              : {
                  ...garantia,
                  ubicacion: newValue.address,
                  latitud: String(newValue.lat),
                  longitud: String(newValue.lng),
                },
          ),
        },
        true,
      );

      formik.setFieldTouched(`garantias[${index}].ubicacion`, true, false);
      return;
    }

    formik.setFieldValue(`garantias[${index}].ubicacion`, newValue.address, false);
    formik.setFieldValue(`garantias[${index}].latitud`, "", false);
    formik.setFieldValue(`garantias[${index}].longitud`, "", false);
  };

  const typeCardOptions = (index: number): OptionCardItem[] =>
    guaranteeTypeOptions.map((option) => ({
      value: option.id,
      ...guaranteeTypeCard(option.code, option.name, formKind(option.id)),
      testId: `garantia-${index + 1}-tipo-${option.code}`,
    }));

  const renderGarantia = (item: GarantiaFormValue, index: number) => {
    const kind = formKind(item.guarantee_type_id);
    const details = detailConfigByType[item.guarantee_type_id] ?? [];
    const bindings: GarantiaFieldBindings = {
      item,
      disabled: isBlocked,
      testId: (name) => `garantia-${index + 1}-${name}`,
      fieldError: (field) => fieldError(index, field),
      touchField: (field) => formik.setFieldTouched(`garantias[${index}].${field}`, true),
      setField: (field, value) => setItemField(index, field, value),
    };

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {!soloTypeId && (
          <FieldWrapper label="Tipo de garantía" full error={fieldError(index, "guarantee_type_id")}>
            <OptionCardGroup
              layout="stack"
              columns={2}
              value={item.guarantee_type_id}
              onChange={(value) => {
                if (canInteract && !isBlocked) requestGuaranteeTypeChange(item.localId, value);
              }}
              options={typeCardOptions(index)}
            />
          </FieldWrapper>
        )}

        {kind === "inmobiliaria" && (
          <FormGarantiaInmobiliariaV2
            {...bindings}
            details={details}
            onUbicacionChange={(value) => handleUbicacionChange(index, value)}
          />
        )}

        {kind === "vehicular" && (
          <FormGarantiaPrendariaV2
            {...bindings}
            details={details}
            catalogos={{
              years: listYears,
              brands: brandsOptions[item.localId] ?? [],
              models: modelsOptions[item.localId] ?? [],
              versions: versionsOptions[item.localId] ?? [],
              loadingBrand: Boolean(loadingBrand[item.localId]),
              loadingModel: Boolean(loadingModel[item.localId]),
              loadingVersion: Boolean(loadingVersion[item.localId]),
            }}
            onVehicleTypeChange={(value) => handleVehicleTypeChange(index, value)}
            onYearChange={(value) => handleYearChange(index, value)}
            onBrandChange={(value) => handleBrandChange(index, value)}
            onModelChange={(value) => handleModelChange(index, value)}
            onVersionChange={(value) => handleVersionChange(index, value)}
          />
        )}
      </Box>
    );
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Garantía"
        title="Información de tu garantía"
        subtitle={
          soloInmobiliaria
            ? "Este crédito es con garantía inmobiliaria. Indícanos los datos del inmueble que la respalda."
            : "Este crédito es con garantía. Indícanos el tipo y los datos del bien que la respalda."
        }
      />

      {isLoading ? (
        <Skeleton variant="rounded" height={220} sx={{ borderRadius: "16px", mb: "14px" }} />
      ) : (
        <>
          {garantias.map((item, index) => (
            <PaperGarantia
              key={item.localId}
              title={garantias.length > 1 ? `Garantía ${index + 1}` : undefined}
              onRemove={() => handleRemoveGarantia(item.localId)}
              removeDisabled={!canInteract || isBlocked}
              removeTestId={`garantia-${index + 1}-quitar-button`}
            >
              {renderGarantia(item, index)}
            </PaperGarantia>
          ))}

          <ButtonAddSlot
            label="Agregar otra garantía"
            onClick={handleAddGarantia}
            disabled={!canInteract || isBlocked || garantias.length >= MAX_GUARANTEES}
            testId="garantia-agregar-button"
          />

          <Box sx={{ mt: 3 }}>
            <Callout tone="neutral">
              <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.dark", mb: 0.5 }}>
                ¿No tienes una garantía?
              </Typography>
              <Typography variant="body3" component="p" color="text.secondary" sx={{ mb: 1.5 }}>
                Quitamos el crédito con garantía de tu solicitud y sigues con los productos sin garantía. Los montos
                máximos son menores, pero el proceso continúa igual.
              </Typography>
              <Button
                tone="outline"
                sx={{ fontSize: 13, px: 2, py: 0.75 }}
                onClick={handleSinGarantia}
                disabled={!nextStatus || isBlocked}
                data-testid="garantia-sin-garantia-button"
              >
                No tengo garantía, continuar sin ella
              </Button>
            </Callout>
          </Box>
        </>
      )}

      <StepActions
        onBack={handleBack}
        onNext={formik.submitForm}
        nextDisabled={isContinueDisabled}
        backTestId="garantia-back-button"
        nextTestId="garantia-continue-button"
      />

      <Dialog
        open={pendingTypeChange !== null}
        onClose={handleCloseTypeChangeDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ textAlign: "center", color: "primary.dark", position: "relative", pr: 6, pt: 3 }}>
          Cambiar tipo de garantía
          <IconButton
            aria-label="Cerrar"
            onClick={handleCloseTypeChangeDialog}
            disabled={isDeletingOnTypeChange}
            sx={{ position: "absolute", right: 8, top: 8, color: "primary.dark" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mb: 3 }}>
            Se eliminará esta garantía al cambiar el tipo de esta garantía.
          </Typography>

          <Box sx={{ display: "flex", gap: 1.5, mb: 1 }}>
            <Button
              tone="ghost"
              fullWidth
              onClick={handleCloseTypeChangeDialog}
              disabled={isDeletingOnTypeChange}
              data-testid="garantia-cambiar-tipo-cancel-button"
            >
              Regresar
            </Button>
            <Button
              fullWidth
              onClick={handleConfirmTypeChange}
              disabled={isDeletingOnTypeChange}
              data-testid="garantia-cambiar-tipo-submit-button"
            >
              Cambiar
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
